<template>
  <div class="am-wizard">

    <!-- ── Steps indicator ── -->
    <div class="am-steps">
      <div
        v-for="(s, i) in steps"
        :key="i"
        class="am-step"
        :class="{
          'am-step--active': step === i,
          'am-step--done': step > i,
        }"
      >
        <div class="am-step__circle">
          <svg v-if="step > i" width="12" height="12" fill="none" viewBox="0 0 24 24">
            <polyline points="20 6 9 17 4 12" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
          </svg>
          <span v-else>{{ i + 1 }}</span>
        </div>
        <span class="am-step__label">{{ s }}</span>
        <div v-if="i < steps.length - 1" class="am-step__line" />
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════════ -->
    <!-- STEP 0: Credenciais -->
    <!-- ══════════════════════════════════════════════════════ -->
    <div v-if="step === 0" class="am-card">
      <div class="am-card__header">
        <div class="am-logo am-logo--ads">
          <feather-icon icon="BarChart2Icon" size="22" />
        </div>
        <div>
          <h3 class="am-card__title">Conectar Ads Metrics</h3>
          <p class="am-card__desc">Informe suas credenciais para integrar com o Ads Metrics.</p>
        </div>
      </div>

      <div class="am-card__body">
        <div class="am-field">
          <label class="am-label">
            <feather-icon icon="UserIcon" size="13" class="mr-50" />
            User ID do Ads Metrics
          </label>
          <b-form-input
            v-model="config.userId"
            placeholder="user_1775228363100_fkgb72ho2"
            class="am-input"
          />
          <span class="am-hint">Encontre em: Ads Metrics → Configurações → API</span>
        </div>

        <div class="am-field">
          <label class="am-label">
            <feather-icon icon="KeyIcon" size="13" class="mr-50" />
            Secret do Webhook
          </label>
          <div class="am-input-group">
            <b-form-input
              v-model="config.secret"
              :type="showSecret ? 'text' : 'password'"
              placeholder="whsec_..."
              class="am-input"
            />
            <button class="am-input-toggle" @click="showSecret = !showSecret">
              <feather-icon :icon="showSecret ? 'EyeOffIcon' : 'EyeIcon'" size="14" />
            </button>
          </div>
        </div>

        <div class="am-field">
          <label class="am-label">
            <feather-icon icon="BriefcaseIcon" size="13" class="mr-50" />
            Ad Account ID
            <span class="am-optional">opcional</span>
          </label>
          <b-form-input
            v-model="config.adAccountId"
            placeholder="act_123456789"
            class="am-input"
          />
          <span class="am-hint">Roteia eventos para uma conta de anúncio específica</span>
        </div>
      </div>

      <div class="am-card__footer">
        <div />
        <button class="am-btn am-btn--primary" :disabled="!config.userId || !config.secret" @click="step = 1">
          Próximo
          <feather-icon icon="ArrowRightIcon" size="14" />
        </button>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════════ -->
    <!-- STEP 1: Mapeamento de Eventos -->
    <!-- ══════════════════════════════════════════════════════ -->
    <div v-if="step === 1" class="am-card">
      <div class="am-card__header">
        <div class="am-logo am-logo--map">
          <feather-icon icon="GitMergeIcon" size="22" />
        </div>
        <div>
          <h3 class="am-card__title">Mapear Eventos</h3>
          <p class="am-card__desc">Selecione quais eventos do VSL Booster devem disparar eventos no Ads Metrics.</p>
        </div>
      </div>

      <div class="am-card__body">
        <div class="am-event-map-header">
          <span class="am-event-map-col">Evento VSL Booster</span>
          <span class="am-event-map-arrow" />
          <span class="am-event-map-col">Evento Ads Metrics</span>
          <span class="am-event-map-col am-event-map-col--sm">Ativo</span>
        </div>

        <div
          v-for="mapping in eventMappings"
          :key="mapping.vslEvent"
          class="am-event-row"
          :class="{ 'am-event-row--active': mapping.enabled }"
        >
          <!-- VSL Booster event -->
          <div class="am-event-cell">
            <div class="am-event-badge am-event-badge--vsl">
              <feather-icon :icon="mapping.icon" size="13" />
              <span>{{ mapping.vslLabel }}</span>
            </div>
            <span class="am-event-desc">{{ mapping.vslDesc }}</span>
          </div>

          <!-- Arrow -->
          <div class="am-event-arrow">
            <svg v-if="mapping.enabled" width="20" height="20" fill="none" viewBox="0 0 24 24">
              <path d="M5 12h14m-4-4l4 4-4 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <svg v-else width="20" height="20" fill="none" viewBox="0 0 24 24" opacity="0.25">
              <path d="M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </div>

          <!-- Ads Metrics event selector -->
          <div class="am-event-cell">
            <select
              v-model="mapping.adsEvent"
              class="am-event-select"
              :disabled="!mapping.enabled"
            >
              <option value="">Selecione...</option>
              <option v-for="ae in adsMetricsEvents" :key="ae.key" :value="ae.key">
                {{ ae.label }}
              </option>
            </select>
            <span v-if="mapping.adsEvent" class="am-event-desc am-event-desc--ads">
              {{ getAdsEventDesc(mapping.adsEvent) }}
            </span>
          </div>

          <!-- Toggle -->
          <div class="am-event-toggle">
            <b-form-checkbox
              v-model="mapping.enabled"
              switch
              class="am-switch"
            />
          </div>
        </div>
      </div>

      <div class="am-card__footer">
        <button class="am-btn am-btn--ghost" @click="step = 0">
          <feather-icon icon="ArrowLeftIcon" size="14" />
          Voltar
        </button>
        <button class="am-btn am-btn--primary" :disabled="!hasAnyMapping" @click="step = 2">
          Próximo
          <feather-icon icon="ArrowRightIcon" size="14" />
        </button>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════════ -->
    <!-- STEP 2: Fluxo e Revisão -->
    <!-- ══════════════════════════════════════════════════════ -->
    <div v-if="step === 2" class="am-card">
      <div class="am-card__header">
        <div class="am-logo am-logo--review">
          <feather-icon icon="CheckCircleIcon" size="22" />
        </div>
        <div>
          <h3 class="am-card__title">Revisar e Ativar</h3>
          <p class="am-card__desc">Confira o mapeamento e selecione o fluxo a monitorar.</p>
        </div>
      </div>

      <div class="am-card__body">
        <!-- Fluxo -->
        <div class="am-field">
          <label class="am-label">
            <feather-icon icon="GitBranchIcon" size="13" class="mr-50" />
            Fluxo monitorado
          </label>
          <select v-model="selectedChat" class="am-select">
            <option value="">Todos os fluxos</option>
            <option v-for="chat in chats" :key="chat.id" :value="chat.id">{{ chat.name }}</option>
          </select>
        </div>

        <!-- Resume -->
        <div class="am-review">
          <div class="am-review__title">
            <feather-icon icon="ListIcon" size="14" class="mr-50" />
            Eventos configurados
          </div>
          <div
            v-for="m in activeMappings"
            :key="m.vslEvent"
            class="am-review__row"
          >
            <span class="am-review__vsl">{{ m.vslLabel }}</span>
            <svg width="16" height="16" fill="none" viewBox="0 0 24 24" class="am-review__arrow">
              <path d="M5 12h14m-4-4l4 4-4 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span class="am-review__ads">{{ getAdsEventLabel(m.adsEvent) }}</span>
          </div>
        </div>

        <!-- Credenciais -->
        <div class="am-review am-review--creds">
          <div class="am-review__title">
            <feather-icon icon="ShieldIcon" size="14" class="mr-50" />
            Credenciais
          </div>
          <div class="am-review__cred">
            <span>User ID:</span>
            <code>{{ config.userId }}</code>
          </div>
          <div class="am-review__cred">
            <span>Secret:</span>
            <code>{{ config.secret.slice(0, 10) }}•••</code>
          </div>
          <div v-if="config.adAccountId" class="am-review__cred">
            <span>Ad Account:</span>
            <code>{{ config.adAccountId }}</code>
          </div>
        </div>
      </div>

      <div class="am-card__footer">
        <button class="am-btn am-btn--ghost" @click="step = 1">
          <feather-icon icon="ArrowLeftIcon" size="14" />
          Voltar
        </button>
        <div class="am-footer-right">
          <button class="am-btn am-btn--outline" @click="testConnection" :disabled="testing">
            <feather-icon icon="SendIcon" size="14" />
            {{ testing ? 'Testando...' : 'Testar conexão' }}
          </button>
          <button class="am-btn am-btn--primary" @click="saveIntegration" :disabled="saving">
            <feather-icon icon="SaveIcon" size="14" />
            {{ saving ? 'Salvando...' : 'Ativar integração' }}
          </button>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════════ -->
    <!-- STEP 3: Sucesso -->
    <!-- ══════════════════════════════════════════════════════ -->
    <div v-if="step === 3" class="am-card am-card--success">
      <div class="am-success">
        <div class="am-success__icon">
          <svg width="36" height="36" fill="none" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
            <polyline points="16 9 10.5 14.5 8 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h3 class="am-success__title">Integração ativada!</h3>
        <p class="am-success__desc">
          O Ads Metrics receberá eventos automaticamente quando seus leads interagirem com seus fluxos.
        </p>
        <div class="am-success__actions">
          <button class="am-btn am-btn--outline" @click="$emit('close')">
            <feather-icon icon="ArrowLeftIcon" size="14" />
            Voltar às integrações
          </button>
          <button class="am-btn am-btn--primary" @click="step = 1">
            <feather-icon icon="EditIcon" size="14" />
            Editar mapeamento
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { BFormCheckbox, BFormInput } from 'bootstrap-vue'
import { useApi } from '@/composables/useApi'

const api = useApi()

export default {
  name: 'AdsMetricsWizard',
  components: { BFormCheckbox, BFormInput },
  props: {
    existingIntegration: { type: Object, default: null },
  },
  data() {
    return {
      step: 0,
      showSecret: false,
      testing: false,
      saving: false,
      integrationId: null,
      steps: ['Credenciais', 'Mapeamento', 'Revisar', 'Pronto'],
      config: {
        userId: '',
        secret: '',
        adAccountId: '',
      },
      selectedChat: '',
      chats: [],
      eventMappings: [
        {
          vslEvent: 'lead::start',
          vslLabel: 'Lead iniciou conversa',
          vslDesc: 'Visitante abriu o chat e começou a interagir',
          icon: 'MessageSquareIcon',
          adsEvent: 'lead.created',
          enabled: true,
        },
        {
          vslEvent: 'lead::finish',
          vslLabel: 'Lead finalizou conversa',
          vslDesc: 'Lead completou todo o fluxo do chat',
          icon: 'CheckCircleIcon',
          adsEvent: 'opportunity.created',
          enabled: true,
        },
        {
          vslEvent: 'lead::email',
          vslLabel: 'Lead informou e-mail',
          vslDesc: 'Lead preencheu o campo de e-mail no chat',
          icon: 'MailIcon',
          adsEvent: 'lead.created',
          enabled: false,
        },
        {
          vslEvent: 'lead::phone',
          vslLabel: 'Lead informou telefone',
          vslDesc: 'Lead preencheu o campo de WhatsApp/telefone',
          icon: 'PhoneIcon',
          adsEvent: 'opportunity.created',
          enabled: false,
        },
        {
          vslEvent: 'lead::cta_click',
          vslLabel: 'Lead clicou no CTA',
          vslDesc: 'Lead clicou no botão de ação (call-to-action)',
          icon: 'MousePointerIcon',
          adsEvent: 'proposal.won',
          enabled: false,
        },
        {
          vslEvent: 'user::signup',
          vslLabel: 'Lead se cadastrou grátis',
          vslDesc: 'Usuário criou conta no período de teste gratuito de 30 dias',
          icon: 'UserPlusIcon',
          adsEvent: 'lead.created',
          enabled: true,
        },
        {
          vslEvent: 'payment::approved',
          vslLabel: 'Pagamento aprovado (Kiwify)',
          vslDesc: 'Lead realizou pagamento via Kiwify e virou cliente',
          icon: 'DollarSignIcon',
          adsEvent: 'proposal.won',
          enabled: true,
        },
        {
          vslEvent: 'video::play',
          vslLabel: 'Vídeo iniciou reprodução',
          vslDesc: 'Visitante deu play no vídeo VSL',
          icon: 'PlayCircleIcon',
          adsEvent: 'lead.created',
          enabled: false,
        },
        {
          vslEvent: 'video::complete',
          vslLabel: 'Vídeo assistido até o final',
          vslDesc: 'Visitante assistiu o vídeo VSL por completo',
          icon: 'AwardIcon',
          adsEvent: 'opportunity.created',
          enabled: false,
        },
      ],
      adsMetricsEvents: [
        { key: 'lead.created', label: 'Lead criado', desc: 'Novo lead adicionado ao CRM', stage: 'Lead' },
        { key: 'opportunity.created', label: 'Oportunidade criada', desc: 'Lead virou oportunidade', stage: 'Oportunidade' },
        { key: 'opportunity.updated', label: 'Oportunidade atualizada', desc: 'Dados da oportunidade atualizados', stage: 'Oportunidade' },
        { key: 'proposal.created', label: 'Proposta enviada', desc: 'Proposta comercial enviada', stage: 'Proposta' },
        { key: 'proposal.won', label: 'Proposta GANHA', desc: 'Proposta aceita — conta como receita', stage: 'Ganho (Won)' },
        { key: 'proposal.lost', label: 'Proposta perdida', desc: 'Proposta recusada', stage: 'Perdido (Lost)' },
        { key: 'customer.created', label: 'Cliente criado', desc: 'Lead convertido em cliente', stage: 'Ganho (Won)' },
        { key: 'sale.completed', label: 'Venda concluída', desc: 'Venda finalizada', stage: 'Ganho (Won)' },
      ],
    }
  },
  computed: {
    userData() {
      return JSON.parse(localStorage.getItem('userData')) || {}
    },
    hasAnyMapping() {
      return this.eventMappings.some(m => m.enabled && m.adsEvent)
    },
    activeMappings() {
      return this.eventMappings.filter(m => m.enabled && m.adsEvent)
    },
  },
  async mounted() {
    await this.loadChats()
    if (this.existingIntegration) {
      this.loadExisting(this.existingIntegration)
    }
  },
  methods: {
    async loadChats() {
      try {
        const { data } = await api.get(`/chat/${this.userData.id}`)
        this.chats = data.filter(c => c.status === 1)
      } catch (e) { console.error(e) }
    },
    loadExisting(int) {
      this.integrationId = int.id
      this.selectedChat = int.id_chat || ''
      try {
        const meta = JSON.parse(int.modelo_metadata || '{}')
        this.config.userId = meta.userId || ''
        this.config.secret = meta.secret || int.api_token || ''
        this.config.adAccountId = meta.adAccountId || ''
        if (meta.eventMappings) {
          for (const mapping of this.eventMappings) {
            if (meta.eventMappings[mapping.vslEvent]) {
              mapping.adsEvent = meta.eventMappings[mapping.vslEvent]
              mapping.enabled = true
            } else {
              mapping.enabled = false
            }
          }
        }
      } catch (e) { /* silent */ }
      this.step = 1
    },
    getAdsEventDesc(key) {
      const e = this.adsMetricsEvents.find(a => a.key === key)
      return e ? `${e.desc} · Estágio: ${e.stage}` : ''
    },
    getAdsEventLabel(key) {
      const e = this.adsMetricsEvents.find(a => a.key === key)
      return e ? e.label : key
    },
    buildEventMappingsObj() {
      const obj = {}
      for (const m of this.activeMappings) {
        obj[m.vslEvent] = m.adsEvent
      }
      return obj
    },
    async testConnection() {
      this.testing = true
      try {
        const { data } = await api.post('/webhook-test', {
          url: 'https://adsmetrics.com.br/api/webhooks/crm',
          payload: {
            userId: this.config.userId,
            secret: this.config.secret,
            event: 'lead.created',
            adAccountId: this.config.adAccountId || undefined,
            data: {
              email: 'teste@vslbooster.com',
              phone: '11999999999',
              name: 'Teste VSL Booster',
              crmName: 'vslbooster',
              crmLeadId: 'test-' + Date.now(),
            },
          },
        })
        if (data.success) {
          this.$message({ type: 'success', message: 'Conexão OK! Evento de teste enviado.' })
        } else {
          this.$message({ type: 'error', message: `Erro: ${data.error || 'Falha na conexão'}` })
        }
      } catch (e) {
        this.$message({ type: 'error', message: 'Erro ao testar conexão' })
      }
      this.testing = false
    },
    async saveIntegration() {
      this.saving = true
      try {
        const metadata = JSON.stringify({
          userId: this.config.userId,
          secret: this.config.secret,
          adAccountId: this.config.adAccountId,
          crmName: 'vslbooster',
          eventMappings: this.buildEventMappingsObj(),
        })
        const eventos = this.activeMappings.map(m => m.vslEvent).join(', ')
        const chatName = this.selectedChat
          ? (this.chats.find(c => c.id === this.selectedChat) || {}).name || ''
          : 'Todos'

        const payload = {
          modelo: 'AdsMetrics',
          fluxos: chatName,
          eventos,
          id_user: this.userData.id,
          id_chat: this.selectedChat || null,
          url: 'https://adsmetrics.com.br/api/webhooks/crm',
          api_token: this.config.secret,
          modelo_metadata: metadata,
          status: 1,
        }

        if (this.integrationId) {
          await api.put(`/Integracoes/${this.integrationId}`, payload)
        } else {
          const { data } = await api.post('/Integracoes', payload)
          this.integrationId = data.id
        }
        this.$message({ type: 'success', message: 'Integração salva com sucesso!' })
        this.step = 3
        this.$emit('saved')
      } catch (e) {
        console.error(e)
        this.$message({ type: 'error', message: 'Erro ao salvar integração' })
      }
      this.saving = false
    },
  },
}
</script>

<style lang="scss" scoped>
$dk-navy: #0D3349;
$dk-orange: #E86A10;
$dk-gold: #F5B935;
$ads-blue: #2563eb;

.am-wizard { display: flex; flex-direction: column; gap: 14px; }

// ── Steps indicator ──
.am-steps {
  display: flex; align-items: center;
  padding: 10px 16px;
  background: #fafbfc; border: 1px solid #edf1f4; border-radius: 8px;
}
.am-step { display: flex; align-items: center; gap: 6px; flex: 1; }
.am-step__circle {
  width: 22px; height: 22px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 10px; font-weight: 800; flex-shrink: 0;
  background: #f0f2f5; color: #9ca5b0;
  border: 1.5px solid #e4eaee; transition: all 0.2s;
}
.am-step--active .am-step__circle { background: $dk-orange; color: #fff; border-color: $dk-orange; }
.am-step--done .am-step__circle { background: #22c55e; color: #fff; border-color: #22c55e; }
.am-step__label { font-size: 11px; font-weight: 600; color: #9ca5b0; white-space: nowrap; }
.am-step--active .am-step__label { color: $dk-navy; font-weight: 700; }
.am-step--done .am-step__label { color: #22c55e; }
.am-step__line { flex: 1; height: 1.5px; margin: 0 6px; background: #e4eaee; border-radius: 1px; }
.am-step--done .am-step__line { background: #22c55e; }

// ── Card ──
.am-card {
  background: #fff; border: 1px solid #edf1f4;
  border-radius: 10px; overflow: hidden;
}
.am-card__header {
  display: flex; align-items: center; gap: 10px;
  padding: 14px 16px; border-bottom: 1px solid #edf1f4;
}
.am-card__title { font-size: 13px; font-weight: 700; color: $dk-navy; margin: 0; }
.am-card__desc { font-size: 11px; color: #9ca5b0; margin: 1px 0 0; }
.am-card__body { padding: 16px; display: flex; flex-direction: column; gap: 14px; }
.am-card__footer {
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 16px; background: #fafbfc;
}

.am-logo {
  width: 34px; height: 34px; border-radius: 8px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  &--ads { background: rgba($ads-blue, 0.08); color: $ads-blue; }
  &--map { background: rgba($dk-orange, 0.08); color: $dk-orange; }
  &--review { background: rgba(#22c55e, 0.08); color: #22c55e; }
}

// ── Fields ──
.am-field { display: flex; flex-direction: column; gap: 4px; }
.am-label {
  display: flex; align-items: center; font-size: 11px; font-weight: 600;
  color: #6b7a8d; text-transform: uppercase; letter-spacing: 0.03em; margin: 0;
}
.am-optional { font-size: 10px; font-weight: 500; color: #b0bac4; text-transform: lowercase; margin-left: 4px; letter-spacing: 0; }
.am-input {
  border: 1px solid #e4eaee !important; border-radius: 7px !important;
  padding: 0 10px !important; height: 36px !important;
  font-size: 12.5px !important; background: #fafbfc !important;
  &:focus { border-color: $ads-blue !important; box-shadow: 0 0 0 2px rgba($ads-blue, 0.08) !important; background: #fff !important; }
}
.am-select {
  border: 1px solid #e4eaee; border-radius: 7px;
  padding: 0 10px; height: 36px; font-size: 12.5px; background: #fafbfc;
  color: $dk-navy; width: 100%; cursor: pointer;
  &:focus { border-color: $ads-blue; outline: none; box-shadow: 0 0 0 2px rgba($ads-blue, 0.08); }
}
.am-hint { font-size: 10.5px; color: #b0bac4; }
.am-input-group { position: relative; display: flex; align-items: center; }
.am-input-group .am-input { padding-right: 36px !important; }
.am-input-toggle {
  position: absolute; right: 8px; background: none; border: none;
  color: #b0bac4; cursor: pointer; padding: 2px;
  &:hover { color: $dk-navy; }
}

// ── Event mapping ──
.am-event-map-header {
  display: grid; grid-template-columns: 1fr 28px 1fr 44px;
  gap: 6px; padding: 4px 10px;
  font-size: 10px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.05em; color: #b0bac4;
}
.am-event-map-col--sm { text-align: center; }
.am-event-row {
  display: grid; grid-template-columns: 1fr 28px 1fr 44px;
  gap: 6px; padding: 8px 10px;
  border: 1px solid #edf1f4; border-radius: 8px; margin-bottom: 5px;
  background: #fafbfc; transition: all 0.15s;
  &--active { border-color: rgba($ads-blue, 0.2); background: rgba($ads-blue, 0.015); }
}
.am-event-cell { display: flex; flex-direction: column; gap: 2px; justify-content: center; }
.am-event-badge {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 11.5px; font-weight: 600; padding: 0;
  &--vsl { color: $dk-navy; }
}
.am-event-desc { font-size: 10px; color: #b0bac4; line-height: 1.2; &--ads { color: $ads-blue; font-weight: 500; } }
.am-event-arrow { display: flex; align-items: center; justify-content: center; color: $dk-orange; }
.am-event-select {
  border: 1px solid #e4eaee; border-radius: 6px;
  padding: 4px 8px; font-size: 11.5px; background: #fff; height: 30px;
  color: $dk-navy; width: 100%; cursor: pointer;
  &:disabled { opacity: 0.35; cursor: not-allowed; background: #f3f6f8; }
  &:focus { border-color: $ads-blue; outline: none; }
}
.am-event-toggle { display: flex; align-items: center; justify-content: center; }
.am-switch ::v-deep .custom-control-input:checked ~ .custom-control-label::before {
  background-color: $ads-blue !important; border-color: $ads-blue !important;
}

// ── Review ──
.am-review {
  background: #fafbfc; border: 1px solid #edf1f4;
  border-radius: 8px; padding: 12px 14px;
  display: flex; flex-direction: column; gap: 6px;
  &--creds { gap: 5px; }
}
.am-review__title {
  display: flex; align-items: center;
  font-size: 10.5px; font-weight: 700; color: #6b7a8d;
  text-transform: uppercase; letter-spacing: 0.04em;
}
.am-review__row {
  display: flex; align-items: center; gap: 8px;
  padding: 6px 10px; background: #fff;
  border-radius: 6px; border: 1px solid #edf1f4;
}
.am-review__vsl { font-size: 12px; font-weight: 600; color: $dk-navy; flex: 1; }
.am-review__arrow { color: $dk-orange; flex-shrink: 0; }
.am-review__ads { font-size: 12px; font-weight: 700; color: $ads-blue; flex: 1; text-align: right; }
.am-review__cred {
  display: flex; align-items: center; justify-content: space-between;
  font-size: 12px; color: #4b5563;
  code { font-size: 11px; background: rgba($dk-navy, 0.05); padding: 1px 6px; border-radius: 3px; color: $dk-navy; }
}

// ── Buttons ──
.am-btn {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 7px 16px; border: none; border-radius: 7px;
  font-size: 12px; font-weight: 700; cursor: pointer; transition: all 0.15s;
  &:disabled { opacity: 0.45; cursor: not-allowed; }
  &--primary {
    background: $ads-blue; color: #fff; box-shadow: 0 2px 8px rgba($ads-blue, 0.25);
    &:hover:not(:disabled) { background: darken($ads-blue, 6%); transform: translateY(-0.5px); }
  }
  &--ghost {
    background: transparent; color: #6b7280;
    &:hover { background: #f3f6f8; }
  }
  &--outline {
    background: #fff; color: $dk-navy; border: 1px solid #e4eaee;
    &:hover:not(:disabled) { border-color: $ads-blue; color: $ads-blue; }
  }
}
.am-footer-right { display: flex; gap: 6px; }

// ── Success ──
.am-card--success { border-color: rgba(#22c55e, 0.2); }
.am-success { text-align: center; padding: 32px 24px; }
.am-success__icon {
  width: 52px; height: 52px; border-radius: 50%; margin: 0 auto 14px;
  background: rgba(#22c55e, 0.08); color: #22c55e;
  display: flex; align-items: center; justify-content: center;
}
.am-success__title { font-size: 14px; font-weight: 800; color: $dk-navy; margin: 0 0 6px; }
.am-success__desc { font-size: 12px; color: #9ca5b0; margin: 0 0 16px; line-height: 1.5; }
.am-success__actions { display: flex; gap: 8px; justify-content: center; }

@media (max-width: 700px) {
  .am-event-map-header { display: none; }
  .am-event-row { grid-template-columns: 1fr; gap: 8px; }
  .am-event-arrow { transform: rotate(90deg); }
  .am-steps { overflow-x: auto; }
}
</style>
